import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, UserCircle, AlertTriangle, TrendingUp, TrendingDown, BarChart3,
  ChevronRight, ChevronDown, ChevronUp, Loader2, Calendar, Target, Gauge,
  Activity, PieChart, Layers, Clock, Maximize2, Minimize2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

// ==========================================
// PERSONA-AWARE GROUPING UTILITIES
// ==========================================

// Get default grouping based on persona
export const getGroupingForPersona = (persona) => {
  // Executive personas default to team-level aggregation
  if (['executive', 'pmo_power', 'sysadmin', 'viewer'].includes(persona)) {
    return 'team';
  }
  // PM personas default to role-level detail
  return 'role';
};

// Determine persona from user roles
export const getPersonaFromRoles = (roles = []) => {
  if (!roles || roles.length === 0) return 'viewer';
  const primaryRole = roles[0]?.role_key || roles[0];
  return primaryRole || 'viewer';
};

// ==========================================
// GROUPING TOGGLE COMPONENT
// ==========================================
const GroupingToggle = ({ grouping, onToggle, tenantId, dashboardType }) => {
  const storageKey = `resourceGrouping_${tenantId}_${dashboardType}`;
  
  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && stored !== grouping) {
        onToggle(stored);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  const handleToggle = () => {
    const newGrouping = grouping === 'team' ? 'role' : 'team';
    try {
      localStorage.setItem(storageKey, newGrouping);
    } catch {
      // localStorage unavailable
    }
    onToggle(newGrouping);
  };

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      data-testid={`grouping-toggle-${dashboardType}`}
      title={`Currently grouped by ${grouping === 'team' ? 'Team' : 'Role'}`}
    >
      {grouping === 'team' ? (
        <Users className="w-3.5 h-3.5" />
      ) : (
        <UserCircle className="w-3.5 h-3.5" />
      )}
      <span>Group by: {grouping === 'team' ? 'Team' : 'Role'}</span>
    </button>
  );
};

// ==========================================
// COLLAPSIBLE WIDGET WRAPPER
// ==========================================
const CollapsibleWidget = ({ 
  title, 
  icon: Icon, 
  iconColor = 'purple',
  tenantId,
  widgetName,
  badge,
  badgeColor,
  children,
  testId
}) => {
  const storageKey = `dashboardWidget_${tenantId}_${widgetName}_collapsed`;
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    try {
      localStorage.setItem(storageKey, String(newState));
    } catch {
      // localStorage may be unavailable
    }
  };

  const iconColorClasses = {
    purple: 'text-purple-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    green: 'text-green-600',
    gray: 'text-gray-600'
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      data-testid={testId}
    >
      <button
        onClick={toggleCollapse}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColorClasses[iconColor]}`} />
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {badge !== undefined && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeColor || 'bg-gray-100 text-gray-700'}`}>
              {badge}
            </span>
          )}
        </div>
        {isCollapsed ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
};

// ==========================================
// RAG STATUS INDICATOR
// ==========================================
const RAGIndicator = ({ value, thresholds = { green: 90, amber: 100 }, showLabel = false, size = 'md' }) => {
  // Green = capacity > demand (under 90%)
  // Amber = ±10% threshold (90-100%)
  // Red = demand > capacity (over 100%)
  let status = 'green';
  let label = 'Healthy';
  
  if (value > thresholds.amber) {
    status = 'red';
    label = 'Over Capacity';
  } else if (value >= thresholds.green) {
    status = 'amber';
    label = 'Near Capacity';
  }

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  const colors = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500'
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className={`${sizeClasses[size]} ${colors[status]} rounded-full`} />
      {showLabel && <span className="text-xs text-gray-600">{label}</span>}
    </div>
  );
};

// ==========================================
// MINI CAPACITY BAR
// ==========================================
const MiniCapacityBar = ({ capacity, demand, label, showPercent = true }) => {
  const utilizationPercent = capacity > 0 ? Math.round((demand / capacity) * 100) : 0;
  const barColor = utilizationPercent > 100 ? 'bg-red-500' : 
                   utilizationPercent >= 90 ? 'bg-amber-500' : 
                   utilizationPercent >= 70 ? 'bg-blue-500' : 'bg-green-500';
  
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-gray-700 truncate">{label}</span>
        {showPercent && (
          <span className={utilizationPercent > 100 ? 'text-red-600 font-bold' : 'text-gray-500'}>
            {utilizationPercent}%
          </span>
        )}
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
        />
      </div>
    </div>
  );
};

// ==========================================
// NO DATA FALLBACK
// ==========================================
const NoDataFallback = ({ message = "Not enough data available (yet)." }) => (
  <div className="py-6 text-center">
    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
      <BarChart3 className="w-6 h-6 text-gray-400" />
    </div>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// ==========================================
// LOADING SKELETON
// ==========================================
const WidgetSkeleton = ({ rows = 3 }) => (
  <div className="animate-pulse space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-8 bg-gray-200 rounded" />
    ))}
  </div>
);

// ==========================================
// 1. PORTFOLIO CAPACITY VS DEMAND WIDGET (Primary KPI)
// ==========================================
export const CapacityVsDemandWidget = ({ 
  tenantId, 
  portfolioId = null, 
  programId = null, 
  projectId = null,
  onNavigate,
  compact = false
}) => {
  const { getCurrency } = useTenant();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      const res = await api.getPortfolioCapacity(tenantId, yearMonth);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load capacity data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={4} />;
  }

  if (error || !data) {
    return <NoDataFallback message="No capacity data available" />;
  }

  const summary = data.summary || {};
  const utilizationPercent = summary.total_capacity > 0 
    ? Math.round(((summary.total_committed_demand || 0) / summary.total_capacity) * 100) 
    : 0;
  
  const deficit = (summary.total_committed_demand || 0) - (summary.total_capacity || 0);
  const hasDeficit = deficit > 0;

  return (
    <div data-testid="capacity-vs-demand-widget">
      {/* Main Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-lg font-bold text-blue-700">{(summary.total_capacity || 0).toFixed(0)}h</p>
          <p className="text-xs text-blue-600">Total Capacity</p>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
          <p className="text-lg font-bold text-purple-700">{(summary.total_committed_demand || 0).toFixed(0)}h</p>
          <p className="text-xs text-purple-600">Committed</p>
        </div>
        <div className={`text-center p-3 rounded-lg border ${hasDeficit ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
          <p className={`text-lg font-bold ${hasDeficit ? 'text-red-700' : 'text-green-700'}`}>
            {hasDeficit ? `-${Math.abs(deficit).toFixed(0)}h` : `+${Math.abs(summary.remaining_availability || 0).toFixed(0)}h`}
          </p>
          <p className={`text-xs ${hasDeficit ? 'text-red-600' : 'text-green-600'}`}>
            {hasDeficit ? 'Deficit' : 'Surplus'}
          </p>
        </div>
      </div>

      {/* RAG Utilization Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 flex items-center gap-2">
            Portfolio Utilization
            <RAGIndicator value={utilizationPercent} showLabel />
          </span>
          <span className={`font-bold ${utilizationPercent > 100 ? 'text-red-600' : utilizationPercent >= 90 ? 'text-amber-600' : 'text-gray-900'}`}>
            {utilizationPercent}%
          </span>
        </div>
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden relative">
          {/* Threshold markers */}
          <div className="absolute top-0 bottom-0 left-[90%] w-px bg-amber-400 z-10" title="90% threshold" />
          <div className="absolute top-0 bottom-0 left-full w-px bg-red-400 z-10" style={{ left: '100%' }} title="100% threshold" />
          <div 
            className={`h-full transition-all ${
              utilizationPercent > 100 ? 'bg-red-500' : 
              utilizationPercent >= 90 ? 'bg-amber-500' : 
              utilizationPercent >= 70 ? 'bg-blue-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>0%</span>
          <span>90%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Bottlenecks Warning */}
      {data.bottlenecks?.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span>{data.bottlenecks.length} Bottleneck{data.bottlenecks.length > 1 ? 's' : ''} Detected</span>
          </div>
          <div className="space-y-1">
            {data.bottlenecks.slice(0, 3).map((b, i) => (
              <div key={i} className="text-xs text-red-600 flex justify-between">
                <span>{b.name}</span>
                <span className="font-bold">{b.total_percent?.toFixed(0) || b.total_demand?.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Link */}
      {!compact && (
        <button 
          onClick={() => onNavigate?.('/resources/portfolio')}
          className="mt-4 w-full text-center text-sm text-purple-600 hover:text-purple-800 flex items-center justify-center gap-1"
        >
          View Details <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ==========================================
// 2. OVERALLOCATED ROLES WIDGET (Top 5)
// ==========================================
export const OverallocatedRolesWidget = ({ tenantId, onNavigate, limit = 5, grouping = 'role' }) => {
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState([]);

  const entityLabel = grouping === 'team' ? 'Team' : 'Role';
  const entityLabelPlural = grouping === 'team' ? 'Teams' : 'Roles';

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.getResourceConflicts(tenantId, null, null);
      // Group by entity and get unique overallocated entities based on grouping
      const entityConflicts = (res.data.conflicts || [])
        .filter(c => c.entity_type === grouping)
        .reduce((acc, c) => {
          if (!acc[c.entity_id]) {
            acc[c.entity_id] = {
              ...c,
              months_affected: [c.month],
              max_over_percent: c.over_by_percent || 0
            };
          } else {
            acc[c.entity_id].months_affected.push(c.month);
            acc[c.entity_id].max_over_percent = Math.max(acc[c.entity_id].max_over_percent, c.over_by_percent || 0);
          }
          return acc;
        }, {});
      
      setConflicts(Object.values(entityConflicts).sort((a, b) => b.max_over_percent - a.max_over_percent).slice(0, limit));
    } catch (err) {
      console.error('Failed to load conflicts:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, limit, grouping]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={limit} />;
  }

  return (
    <div data-testid="overallocated-roles-widget">
      {conflicts.length === 0 ? (
        <div className="py-6 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-sm text-gray-600">No overallocations detected</p>
          <p className="text-xs text-gray-400 mt-1">All {entityLabelPlural.toLowerCase()} within capacity</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conflicts.map((c, i) => (
            <div 
              key={i}
              className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors border border-red-100"
              onClick={() => onNavigate?.('/resources/explorer')}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-red-700">#{i + 1}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-red-900">{c.entity_name}</span>
                  <p className="text-xs text-red-600">{c.months_affected?.length} month(s) affected</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-red-700">+{c.max_over_percent?.toFixed(0)}%</span>
                <p className="text-xs text-red-500">over capacity</p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <button 
        onClick={() => onNavigate?.('/resources/utilisation')}
        className="mt-3 w-full text-center text-xs text-purple-600 hover:text-purple-800"
      >
        View All {entityLabelPlural}
      </button>
    </div>
  );
};

// ==========================================
// 3. PORTFOLIO BOTTLENECKS WIDGET
// ==========================================
export const PortfolioBottlenecksWidget = ({ tenantId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [affectedProjects, setAffectedProjects] = useState({});

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      const [capacityRes, conflictsRes] = await Promise.all([
        api.getPortfolioCapacity(tenantId, yearMonth),
        api.getResourceConflicts(tenantId, null, null)
      ]);
      
      setBottlenecks(capacityRes.data.bottlenecks || []);
      
      // Map conflicts to affected projects
      const projectMap = {};
      (conflictsRes.data.conflicts || []).forEach(c => {
        if (c.entity_id && c.project_ids) {
          projectMap[c.entity_id] = {
            count: c.project_ids.length,
            names: c.project_names || []
          };
        }
      });
      setAffectedProjects(projectMap);
    } catch (err) {
      console.error('Failed to load bottlenecks:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={3} />;
  }

  return (
    <div data-testid="portfolio-bottlenecks-widget">
      {bottlenecks.length === 0 ? (
        <div className="py-6 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Target className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-sm text-green-600">No bottlenecks this month</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bottlenecks.map((b, i) => {
            const projectInfo = affectedProjects[b.id] || { count: 0, names: [] };
            return (
              <div 
                key={i} 
                className="p-3 bg-amber-50 rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => onNavigate?.('/resources/explorer')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-amber-900">{b.name}</span>
                  <span className="text-sm font-bold text-red-600">{b.total_percent?.toFixed(0)}%</span>
                </div>
                {projectInfo.count > 0 && (
                  <p className="text-xs text-amber-700">
                    Affects {projectInfo.count} project{projectInfo.count > 1 ? 's' : ''}
                    {projectInfo.names.length > 0 && `: ${projectInfo.names.slice(0, 2).join(', ')}${projectInfo.names.length > 2 ? '...' : ''}`}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==========================================
// 4. PROGRAM UTILIZATION HEAT SUMMARY
// ==========================================
export const ProgramUtilisationWidget = ({ tenantId, programId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ under: 0, normal: 0, over: 0 });
  const [monthRange, setMonthRange] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const now = new Date();
      const startMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 5);
      const endMonth = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;
      
      const res = await api.getUtilisationHeatmap(tenantId, startMonth, endMonth, null);
      
      const months = res.data.months || [];
      setMonthRange(months);
      
      // Count utilization states
      let under = 0, normal = 0, over = 0;
      (res.data.heatmap || []).forEach(entity => {
        months.forEach(m => {
          const status = entity.months?.[m]?.status;
          if (status === 'under') under++;
          else if (status === 'normal' || status === 'high') normal++;
          else if (status === 'over') over++;
        });
      });
      
      setData({ under, normal, over });
    } catch (err) {
      console.error('Failed to load utilisation data:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={2} />;
  }

  const total = data.under + data.normal + data.over;

  return (
    <div data-testid="program-utilisation-widget">
      {/* State bars */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600 flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded" />
              Under-utilized
            </span>
            <span className="font-medium text-gray-900">{data.under}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: total > 0 ? `${(data.under / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
        
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600 flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              Normal
            </span>
            <span className="font-medium text-gray-900">{data.normal}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all"
              style={{ width: total > 0 ? `${(data.normal / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
        
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600 flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded" />
              Over-allocated
            </span>
            <span className="font-medium text-gray-900">{data.over}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-500 transition-all"
              style={{ width: total > 0 ? `${(data.over / total) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </div>
      
      <p className="text-xs text-gray-400 mt-3 text-center">
        {monthRange.length} months analyzed
      </p>
    </div>
  );
};

// ==========================================
// 5. PROGRAM DEMAND TREND (Line Chart)
// ==========================================
export const ProgramDemandTrendWidget = ({ tenantId, programId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const now = new Date();
      const months = [];
      for (let i = -3; i <= 8; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      
      const res = await api.getUtilisationHeatmap(tenantId, months[0], months[months.length - 1], null);
      
      // Calculate total demand per month
      const demandByMonth = {};
      months.forEach(m => { demandByMonth[m] = 0; });
      
      (res.data.heatmap || []).forEach(entity => {
        months.forEach(m => {
          demandByMonth[m] += entity.months?.[m]?.allocated || 0;
        });
      });
      
      setTrendData(months.map(m => ({
        month: m,
        label: new Date(m + '-01').toLocaleDateString('en-US', { month: 'short' }),
        demand: demandByMonth[m]
      })));
    } catch (err) {
      console.error('Failed to load trend data:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={4} />;
  }

  if (trendData.length === 0) {
    return <NoDataFallback message="No demand trend data available" />;
  }

  const maxDemand = Math.max(...trendData.map(d => d.demand), 1);
  
  return (
    <div data-testid="program-demand-trend-widget">
      {/* Simple bar chart */}
      <div className="flex items-end gap-1 h-32">
        {trendData.map((d, i) => {
          const height = (d.demand / maxDemand) * 100;
          const isCurrentMonth = i === 3; // 4th item is current month
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center">
              <div 
                className={`w-full rounded-t transition-all ${
                  isCurrentMonth ? 'bg-purple-500' : 'bg-blue-400 hover:bg-blue-500'
                }`}
                style={{ height: `${height}%`, minHeight: d.demand > 0 ? '4px' : '0px' }}
                title={`${d.demand.toFixed(0)}h`}
              />
              <span className={`text-[10px] mt-1 ${isCurrentMonth ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
      
      <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
        <span>Past 3 months</span>
        <span className="flex items-center gap-1">
          {trendData[trendData.length - 1]?.demand > trendData[3]?.demand ? (
            <>
              <TrendingUp className="w-3 h-3 text-amber-500" />
              <span className="text-amber-600">Rising</span>
            </>
          ) : (
            <>
              <TrendingDown className="w-3 h-3 text-green-500" />
              <span className="text-green-600">Stable</span>
            </>
          )}
        </span>
        <span>Next 8 months</span>
      </div>
    </div>
  );
};

// ==========================================
// 6. PROGRAM CONFLICTS LIST
// ==========================================
export const ProgramConflictsWidget = ({ tenantId, programId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.getResourceConflicts(tenantId, null, null);
      setConflicts((res.data.conflicts || []).slice(0, 5));
    } catch (err) {
      console.error('Failed to load conflicts:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={3} />;
  }

  return (
    <div data-testid="program-conflicts-widget">
      {conflicts.length === 0 ? (
        <div className="py-6 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Activity className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-sm text-green-600">No resource conflicts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conflicts.map((c, i) => (
            <div 
              key={i}
              className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => onNavigate?.('/resources/explorer')}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{c.entity_name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {c.message || `Overallocated across ${c.project_count || 0} projects in ${c.month}`}
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  c.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {c.severity}
                </span>
              </div>
              {c.project_names?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.project_names.slice(0, 3).map((name, j) => (
                    <span key={j} className="text-xs bg-white px-2 py-0.5 rounded border border-gray-200">
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// 7. PROJECT DEMAND VS CAPACITY
// ==========================================
export const ProjectDemandVsCapacityWidget = ({ tenantId, projectId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId || !projectId) return;
    setLoading(true);
    try {
      const res = await api.listAllocations(tenantId, { project_id: projectId });
      setAllocations(res.data.allocations || []);
    } catch (err) {
      console.error('Failed to load allocations:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={4} />;
  }

  if (allocations.length === 0) {
    return <NoDataFallback message="No resource allocations for this project" />;
  }

  // Group by role/team
  const grouped = allocations.reduce((acc, a) => {
    const key = a.role_name || a.team_name || 'Unassigned';
    if (!acc[key]) {
      acc[key] = { name: key, total: 0, type: a.role_id ? 'role' : 'team' };
    }
    acc[key].total += a.allocation_value || 0;
    return acc;
  }, {});

  const items = Object.values(grouped).sort((a, b) => b.total - a.total);

  return (
    <div data-testid="project-demand-vs-capacity-widget">
      <div className="space-y-3">
        {items.slice(0, 6).map((item, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-700 truncate">{item.name}</span>
              <span className={`font-medium ${item.total > 100 ? 'text-red-600' : 'text-gray-900'}`}>
                {item.total.toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  item.total > 100 ? 'bg-red-500' : 
                  item.total >= 80 ? 'bg-amber-500' : 
                  'bg-blue-500'
                }`}
                style={{ width: `${Math.min(item.total, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      
      {items.length > 6 && (
        <p className="text-xs text-gray-400 mt-3 text-center">
          +{items.length - 6} more allocations
        </p>
      )}
    </div>
  );
};

// ==========================================
// 8. ROLE HEATMAP (Mini for Project)
// ==========================================
export const RoleHeatmapWidget = ({ tenantId, projectId, onNavigate, grouping = 'role' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [months, setMonths] = useState([]);

  const entityLabel = grouping === 'team' ? 'Team' : 'Role';

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const now = new Date();
      const startMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 3);
      const endMonth = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}`;
      
      const res = await api.getUtilisationHeatmap(tenantId, startMonth, endMonth, grouping);
      setData(res.data.heatmap?.slice(0, 5) || []);
      setMonths(res.data.months?.slice(0, 4) || []);
    } catch (err) {
      console.error('Failed to load heatmap:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, grouping]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'over': return 'bg-red-500 text-white';
      case 'high': return 'bg-amber-500 text-white';
      case 'normal': return 'bg-blue-500 text-white';
      case 'under': return 'bg-green-500 text-white';
      default: return 'bg-gray-200 text-gray-500';
    }
  };

  if (loading) {
    return <WidgetSkeleton rows={5} />;
  }

  if (data.length === 0) {
    return <NoDataFallback message="No role utilization data" />;
  }

  return (
    <div data-testid="role-heatmap-widget">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left py-2 pr-2 w-24 font-medium text-gray-600"></th>
              {months.map(m => (
                <th key={m} className="text-center py-2 px-1 font-medium text-gray-600">
                  {new Date(m + '-01').toLocaleDateString('en-US', { month: 'short' })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(entity => (
              <tr key={entity.id}>
                <td className="py-1.5 pr-2 truncate max-w-[80px] text-gray-700" title={entity.name}>
                  {entity.name}
                </td>
                {months.map(m => {
                  const monthData = entity.months?.[m];
                  return (
                    <td key={m} className="py-1.5 px-1">
                      <div 
                        className={`w-10 h-7 rounded flex items-center justify-center text-[11px] font-bold mx-auto ${getStatusColor(monthData?.status)}`}
                        title={`${monthData?.utilisation || 0}% utilized`}
                      >
                        {monthData?.utilisation || 0}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded" /> Under</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded" /> Normal</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded" /> High</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded" /> Over</span>
      </div>
    </div>
  );
};

// ==========================================
// 9. ALLOCATION SUMMARY
// ==========================================
export const AllocationSummaryWidget = ({ tenantId, projectId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId || !projectId) return;
    setLoading(true);
    try {
      const res = await api.listAllocations(tenantId, { project_id: projectId });
      setAllocations(res.data.allocations || []);
    } catch (err) {
      console.error('Failed to load allocations:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={4} />;
  }

  if (allocations.length === 0) {
    return <NoDataFallback message="No allocations assigned" />;
  }

  return (
    <div data-testid="allocation-summary-widget">
      <div className="divide-y divide-gray-100">
        {allocations.slice(0, 6).map((a, i) => (
          <div key={i} className="py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${a.is_hard ? 'bg-purple-500' : 'bg-gray-400'}`} />
              <span className="text-sm text-gray-700">{a.role_name || a.team_name}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-gray-900">
                {a.allocation_type === 'hours' ? `${a.allocation_value}h` : `${a.allocation_value}%`}
              </span>
              <p className="text-xs text-gray-400">
                {new Date(a.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
                {new Date(a.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      {allocations.length > 6 && (
        <button 
          onClick={() => onNavigate?.(`/resources/explorer?project=${projectId}`)}
          className="mt-2 w-full text-center text-xs text-purple-600 hover:text-purple-800"
        >
          View all {allocations.length} allocations
        </button>
      )}
    </div>
  );
};

// ==========================================
// 10. TEAM ASSIGNMENT OVERVIEW
// ==========================================
export const TeamAssignmentWidget = ({ tenantId, projectId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);

  const loadData = useCallback(async () => {
    if (!tenantId || !projectId) return;
    setLoading(true);
    try {
      const res = await api.listAllocations(tenantId, { project_id: projectId });
      const allocations = res.data.allocations || [];
      
      // Group by team
      const teamMap = {};
      allocations.filter(a => a.team_id).forEach(a => {
        if (!teamMap[a.team_id]) {
          teamMap[a.team_id] = {
            id: a.team_id,
            name: a.team_name || 'Unknown',
            totalPercent: 0,
            allocations: []
          };
        }
        teamMap[a.team_id].totalPercent += a.allocation_value || 0;
        teamMap[a.team_id].allocations.push(a);
      });
      
      setTeams(Object.values(teamMap).sort((a, b) => b.totalPercent - a.totalPercent));
    } catch (err) {
      console.error('Failed to load team assignments:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <WidgetSkeleton rows={3} />;
  }

  if (teams.length === 0) {
    return <NoDataFallback message="No teams assigned to this project" />;
  }

  return (
    <div data-testid="team-assignment-widget">
      <div className="space-y-3">
        {teams.map((team, i) => {
          const workloadLevel = team.totalPercent > 100 ? 'high' : 
                               team.totalPercent >= 70 ? 'medium' : 'low';
          const workloadColors = {
            high: 'bg-red-100 text-red-700 border-red-200',
            medium: 'bg-amber-100 text-amber-700 border-amber-200',
            low: 'bg-green-100 text-green-700 border-green-200'
          };
          
          return (
            <div 
              key={i}
              className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => onNavigate?.(`/resources/teams/${team.id}`)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">{team.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${workloadColors[workloadLevel]}`}>
                  {workloadLevel} workload
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{team.allocations.length} allocation{team.allocations.length > 1 ? 's' : ''}</span>
                <span className="font-medium">{team.totalPercent.toFixed(0)}% total</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// COMPOSITE SECTIONS FOR DASHBOARDS
// ==========================================

// Executive Dashboard Resource Section (Team-level aggregation by default)
export const ExecutiveResourceSection = ({ tenantId, onNavigate }) => {
  const { user } = useAuth();
  const persona = getPersonaFromRoles(user?.roles);
  const defaultGrouping = getGroupingForPersona(persona);
  
  const [grouping, setGrouping] = useState(defaultGrouping);

  return (
    <div className="space-y-4" data-testid="executive-resource-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Resource Management
        </h2>
        <div className="flex items-center gap-3">
          <GroupingToggle 
            grouping={grouping} 
            onToggle={setGrouping} 
            tenantId={tenantId}
            dashboardType="executive"
          />
          <button 
            onClick={() => onNavigate?.('/resources')}
            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
          >
            Open Resources <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <CollapsibleWidget
            title="Portfolio Capacity vs Demand"
            icon={Gauge}
            iconColor="purple"
            tenantId={tenantId}
            widgetName="capacity_demand"
            testId="widget-capacity-demand"
          >
            <CapacityVsDemandWidget tenantId={tenantId} onNavigate={onNavigate} />
          </CollapsibleWidget>
        </div>
        
        <CollapsibleWidget
          title={grouping === 'team' ? 'Overallocated Teams' : 'Overallocated Roles'}
          icon={AlertTriangle}
          iconColor="red"
          tenantId={tenantId}
          widgetName="overallocated_roles"
          testId="widget-overallocated-roles"
        >
          <OverallocatedRolesWidget tenantId={tenantId} onNavigate={onNavigate} limit={5} grouping={grouping} />
        </CollapsibleWidget>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CollapsibleWidget
          title="Portfolio Bottlenecks"
          icon={Target}
          iconColor="amber"
          tenantId={tenantId}
          widgetName="bottlenecks"
          testId="widget-bottlenecks"
        >
          <PortfolioBottlenecksWidget tenantId={tenantId} onNavigate={(path) => {
            // Pre-filter navigation to /resources with context
            if (path === '/resources/explorer') {
              onNavigate?.('/resources/explorer');
            } else {
              onNavigate?.(path);
            }
          }} />
        </CollapsibleWidget>
        
        <CollapsibleWidget
          title={grouping === 'team' ? 'Team Utilization Heatmap' : 'Role Utilization Heatmap'}
          icon={BarChart3}
          iconColor="blue"
          tenantId={tenantId}
          widgetName="utilization_heatmap"
          testId="widget-utilization-heatmap"
        >
          <RoleHeatmapWidget tenantId={tenantId} onNavigate={onNavigate} grouping={grouping} />
        </CollapsibleWidget>
      </div>
    </div>
  );
};

// Program Dashboard Resource Section
export const ProgramResourceSection = ({ tenantId, programId, onNavigate }) => {
  return (
    <div className="space-y-4" data-testid="program-resource-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-600" />
          Resource Overview
        </h2>
        <button 
          onClick={() => onNavigate?.('/resources')}
          className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
        >
          Full View <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CollapsibleWidget
          title="Utilization Summary"
          icon={PieChart}
          iconColor="blue"
          tenantId={tenantId}
          widgetName="program_utilisation"
          testId="widget-program-utilisation"
        >
          <ProgramUtilisationWidget tenantId={tenantId} programId={programId} onNavigate={onNavigate} />
        </CollapsibleWidget>
        
        <CollapsibleWidget
          title="Demand Trend"
          icon={TrendingUp}
          iconColor="purple"
          tenantId={tenantId}
          widgetName="program_demand_trend"
          testId="widget-program-demand-trend"
        >
          <ProgramDemandTrendWidget tenantId={tenantId} programId={programId} onNavigate={onNavigate} />
        </CollapsibleWidget>
        
        <CollapsibleWidget
          title="Resource Conflicts"
          icon={AlertTriangle}
          iconColor="red"
          tenantId={tenantId}
          widgetName="program_conflicts"
          testId="widget-program-conflicts"
        >
          <ProgramConflictsWidget tenantId={tenantId} programId={programId} onNavigate={onNavigate} />
        </CollapsibleWidget>
      </div>
    </div>
  );
};

// Project Dashboard Resource Section (Role-level aggregation by default for PM view)
export const ProjectResourceSection = ({ tenantId, projectId, onNavigate }) => {
  const { user } = useAuth();
  const persona = getPersonaFromRoles(user?.roles);
  // For project dashboard, default to role-level regardless of persona
  const [grouping, setGrouping] = useState('role');

  return (
    <div className="space-y-4" data-testid="project-resource-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-600" />
          Resource Assignments
        </h2>
        <div className="flex items-center gap-3">
          <GroupingToggle 
            grouping={grouping} 
            onToggle={setGrouping} 
            tenantId={tenantId}
            dashboardType="project"
          />
          <button 
            onClick={() => onNavigate?.('/resources/explorer')}
            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
          >
            Manage <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CollapsibleWidget
          title="Demand vs Capacity"
          icon={Gauge}
          iconColor="purple"
          tenantId={tenantId}
          widgetName="project_demand_capacity"
          testId="widget-project-demand-capacity"
        >
          <ProjectDemandVsCapacityWidget tenantId={tenantId} projectId={projectId} onNavigate={onNavigate} />
        </CollapsibleWidget>
        
        <CollapsibleWidget
          title={grouping === 'team' ? 'Team Heatmap' : 'Role Heatmap'}
          icon={BarChart3}
          iconColor="blue"
          tenantId={tenantId}
          widgetName="project_role_heatmap"
          testId="widget-project-role-heatmap"
        >
          <RoleHeatmapWidget tenantId={tenantId} projectId={projectId} onNavigate={onNavigate} grouping={grouping} />
        </CollapsibleWidget>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CollapsibleWidget
          title="Allocation Summary"
          icon={Clock}
          iconColor="gray"
          tenantId={tenantId}
          widgetName="project_allocation_summary"
          testId="widget-project-allocation-summary"
        >
          <AllocationSummaryWidget tenantId={tenantId} projectId={projectId} onNavigate={onNavigate} />
        </CollapsibleWidget>
        
        <CollapsibleWidget
          title="Team Assignments"
          icon={Users}
          iconColor="green"
          tenantId={tenantId}
          widgetName="project_team_assignment"
          testId="widget-project-team-assignment"
        >
          <TeamAssignmentWidget tenantId={tenantId} projectId={projectId} onNavigate={onNavigate} />
        </CollapsibleWidget>
      </div>
    </div>
  );
};

// Default export for backwards compatibility
export default ExecutiveResourceSection;
